import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, V2Button } from '@sakhtar/design-system/react';
import type { AiScopeItem } from '../aiPreview.js';
import { faInt } from './format.js';
import { PERIOD_OPTIONS } from './sample.js';
import type { PeriodId, ScopeKey } from './types.js';
import { useModalSheet } from './useModalSheet.js';

export interface ComposerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** The caller's REAL scope; modules without access are shown but never selectable. */
  readonly scope: readonly AiScopeItem[];
  /** Modules ticked for this question (subset of the granted ones). */
  readonly selected: readonly ScopeKey[];
  readonly onToggleScope: (key: ScopeKey) => void;
  readonly period: PeriodId;
  readonly onPeriodChange: (period: PeriodId) => void;
  /** «برآورد مصرف: حدود N توکن» — integer. */
  readonly estimate: number;
  /**
   * Blocking states (frames I–L) override these: `disabled` greys the whole
   * composer, `notice` renders above it (e.g. «موجودی توکن تمام شده است» with a
   * purchase button), `placeholder` replaces the hint text, `sendLabel` the button.
   */
  readonly disabled?: boolean;
  /** An answer is being produced: input stays editable, sending waits. */
  readonly busy?: boolean;
  readonly notice?: ReactNode | undefined;
  readonly placeholder?: string | undefined;
  readonly sendLabel?: string | undefined;
  /** The central assistant also accepts general questions without an ERP scope. */
  readonly allowGeneral?: boolean;
  readonly deepSearchEnabled?: boolean | undefined;
  readonly onToggleDeepSearch?: (() => void) | undefined;
  readonly deepSearchScope?: 'web' | 'company' | 'hybrid' | undefined;
  readonly onDeepSearchScopeChange?: ((scope: 'web' | 'company' | 'hybrid') => void) | undefined;
}

const DEFAULT_PLACEHOLDER = 'مثلاً: فروش شهریور را با شهریور سال قبل مقایسه کن';

/** Composer: textarea, send button, independent deep-search control, and collapsible settings. */
export function Composer({
  value,
  onChange,
  onSubmit,
  scope,
  selected,
  onToggleScope,
  period,
  onPeriodChange,
  estimate,
  disabled = false,
  busy = false,
  notice,
  placeholder = DEFAULT_PLACEHOLDER,
  sendLabel = 'ارسال',
  allowGeneral = false,
  deepSearchEnabled = false,
  onToggleDeepSearch,
  deepSearchScope = 'hybrid',
  onDeepSearchScopeChange,
}: ComposerProps) {
  const inputId = useId();
  const [optionsOpen, setOptionsOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement | null>(null);
  useModalSheet(optionsOpen, sheetRef, () => setOptionsOpen(false));

  const canSend =
    !disabled && !busy && value.trim() !== '' && (allowGeneral || selected.length > 0);

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    if (canSend) onSubmit();
  }

  return (
    <form
      className="ai-composer"
      data-disabled={disabled}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSend) onSubmit();
      }}
    >
      {notice ? <div className="ai-composer__notice">{notice}</div> : null}

      {/* Main input row: textarea and send button side-by-side without overlap */}
      <div className="ai-composer__main">
        <label htmlFor={inputId} className="ai-visually-hidden">
          پرسش شما
        </label>
        <textarea
          id={inputId}
          className="sk-textarea ai-composer__input"
          rows={1}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <V2Button
          type="submit"
          variant="primary"
          className="ai-composer__send"
          disabled={!canSend}
          aria-label={sendLabel}
        >
          <span className="ai-composer__send-text">{sendLabel}</span>
          <Icon name="chevronLeft" size="sm" className="ai-composer__send-icon" />
        </V2Button>
      </div>

      {/* Toolbar row: deep-search toggle, mobile options trigger, desktop inline controls & estimate */}
      <div className="ai-composer__toolbar">
        {onToggleDeepSearch ? (
          <button
            type="button"
            className={`sk-btn sk-btn--sm ${
              deepSearchEnabled ? 'sk-btn--primary ai-deep-search-btn--active' : 'sk-btn--secondary'
            } ai-composer__tool-btn ai-deep-search-toggle`}
            aria-pressed={deepSearchEnabled}
            disabled={disabled}
            onClick={onToggleDeepSearch}
            title="فعال‌سازی جست‌وجوی عمیق و چندمرحله‌ای"
          >
            <Icon name="search" size="sm" />
            <span>جست‌وجوی عمیق</span>
            {deepSearchEnabled ? (
              <span className="ai-composer__active-dot" aria-hidden="true" />
            ) : null}
          </button>
        ) : null}

        <button
          type="button"
          className="sk-btn sk-btn--sm sk-btn--secondary ai-composer__tool-btn ai-composer__scope-trigger"
          disabled={disabled}
          onClick={() => setOptionsOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={optionsOpen}
          title="تنظیم فیلترها و دامنه اطلاعات"
        >
          <Icon name="filter" size="sm" />
          <span>فیلترها و بازه</span>
          {selected.length > 0 && !allowGeneral ? (
            <span className="sk-tag sk-tag--sm ai-composer__scope-count">
              {faInt(selected.length)}
            </span>
          ) : null}
        </button>

        {/* Desktop inline controls (hidden on mobile <= 899px via CSS) */}
        <div className="ai-composer__desktop-controls">
          <div
            className="ai-composer__scope"
            role="group"
            aria-label={allowGeneral ? 'دسترسی‌های اطلاعات شرکت' : 'محدودهٔ این پرسش'}
          >
            {scope.map((item) => {
              if (!item.granted) {
                return (
                  <span
                    key={item.key}
                    className="sk-badge sk-badge--neutral ai-scope-chip ai-scope-chip--locked"
                    title={`${item.label} — بدون دسترسی`}
                    aria-disabled="true"
                  >
                    <Icon name="lock" size="sm" />
                    {item.label}
                    <span className="ai-visually-hidden"> — بدون دسترسی</span>
                  </span>
                );
              }
              if (allowGeneral) {
                return (
                  <span
                    key={item.key}
                    className="sk-badge sk-badge--neutral ai-scope-chip"
                    title="اطلاعات مرتبط با پرسش، در محدودهٔ مجوزهای شما بررسی می‌شود"
                  >
                    {item.label}
                  </span>
                );
              }
              const on = selected.includes(item.key);
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`sk-badge ai-scope-chip ${on ? 'sk-badge--brand' : 'sk-badge--neutral'}`}
                  aria-pressed={on}
                  disabled={disabled}
                  onClick={() => onToggleScope(item.key)}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className="ai-composer__controls">
            {deepSearchEnabled && onDeepSearchScopeChange ? (
              <select
                className="sk-select ai-composer__period ai-composer__deep-scope"
                aria-label="دامنه جست‌وجوی عمیق"
                value={deepSearchScope}
                disabled={disabled}
                onChange={(event) =>
                  onDeepSearchScopeChange(event.target.value as 'web' | 'company' | 'hybrid')
                }
              >
                <option value="hybrid">ترکیبی (وب و سازمان)</option>
                <option value="web">وب آزاد</option>
                <option value="company">دانش سازمان</option>
              </select>
            ) : null}
            {!allowGeneral ? (
              <select
                className="sk-select ai-composer__period"
                aria-label="بازهٔ زمانی"
                value={period}
                disabled={disabled}
                onChange={(event) => onPeriodChange(event.target.value as PeriodId)}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        </div>

        <div className="ai-composer__estimate">
          {allowGeneral ? (
            'برای گزارش شرکت، موضوع و بازهٔ زمانی را در پرسش بنویسید؛ هزینه بر اساس مصرف واقعی محاسبه می‌شود.'
          ) : selected.length === 0 ? (
            'حداقل یک محدودهٔ داده را برای این پرسش انتخاب کنید.'
          ) : (
            <>
              برآورد مصرف: حدود <span className="sk-num">{faInt(estimate)}</span> توکن
            </>
          )}
        </div>
      </div>

      {/* V2 Scope & Options Bottom Sheet (Mobile drawer) */}
      {optionsOpen ? (
        <div
          className="ai-composer__sheet-backdrop"
          onClick={() => setOptionsOpen(false)}
          role="presentation"
        >
          <div
            ref={sheetRef}
            tabIndex={-1}
            className="ai-composer__sheet sk-card"
            role="dialog"
            aria-modal="true"
            aria-label="تنظیمات محدودهٔ گفتگو و بازهٔ زمانی"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ai-composer__sheet-head">
              <div className="ai-composer__sheet-handle" />
              <div className="ai-composer__sheet-title-row">
                <h3 className="ai-composer__sheet-title">محدودهٔ داده‌ها و تنظیمات پرسش</h3>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                  onClick={() => setOptionsOpen(false)}
                  aria-label="بستن تنظیمات"
                >
                  <Icon name="x" size="sm" />
                </button>
              </div>
            </div>
            <div className="ai-composer__sheet-body">
              <div className="ai-composer__sheet-section">
                <label className="ai-composer__sheet-label">
                  {allowGeneral ? 'دسترسی‌های اطلاعات سازمان:' : 'محدودهٔ اطلاعات برای پاسخ:'}
                </label>
                <div className="ai-composer__sheet-chips">
                  {scope.map((item) => {
                    if (!item.granted) {
                      return (
                        <span
                          key={item.key}
                          className="sk-badge sk-badge--neutral ai-scope-chip ai-scope-chip--locked"
                          aria-disabled="true"
                        >
                          <Icon name="lock" size="sm" />
                          {item.label} (بدون دسترسی)
                        </span>
                      );
                    }
                    if (allowGeneral) {
                      return (
                        <span key={item.key} className="sk-badge sk-badge--neutral ai-scope-chip">
                          {item.label}
                        </span>
                      );
                    }
                    const on = selected.includes(item.key);
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={`sk-badge ai-scope-chip ${on ? 'sk-badge--brand' : 'sk-badge--neutral'}`}
                        aria-pressed={on}
                        onClick={() => onToggleScope(item.key)}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {!allowGeneral ? (
                <div className="ai-composer__sheet-section">
                  <label htmlFor="ai-sheet-period" className="ai-composer__sheet-label">
                    بازهٔ زمانی داده‌ها:
                  </label>
                  <select
                    id="ai-sheet-period"
                    className="sk-select ai-composer__sheet-select"
                    value={period}
                    onChange={(e) => onPeriodChange(e.target.value as PeriodId)}
                  >
                    {PERIOD_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {deepSearchEnabled && onDeepSearchScopeChange ? (
                <div className="ai-composer__sheet-section">
                  <label htmlFor="ai-sheet-deep-scope" className="ai-composer__sheet-label">
                    دامنهٔ جست‌وجوی عمیق:
                  </label>
                  <select
                    id="ai-sheet-deep-scope"
                    className="sk-select ai-composer__sheet-select"
                    value={deepSearchScope}
                    onChange={(e) =>
                      onDeepSearchScopeChange(e.target.value as 'web' | 'company' | 'hybrid')
                    }
                  >
                    <option value="hybrid">ترکیبی (وب و سازمان)</option>
                    <option value="web">وب آزاد</option>
                    <option value="company">دانش سازمان</option>
                  </select>
                </div>
              ) : null}
            </div>
            <div className="ai-composer__sheet-foot">
              <V2Button
                variant="primary"
                size="md"
                className="ai-composer__sheet-done"
                onClick={() => setOptionsOpen(false)}
              >
                تأیید و بازگشت
              </V2Button>
            </div>
          </div>
        </div>
      ) : null}
    </form>
  );
}
